import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

export default function AdminAccounting() {
  const { currentCompany } = useApp();
  const [tab, setTab] = useState('deadlines');
  const { t } = useI18n();
  const [deadlines, setDeadlines] = useState({ upcoming_declarations: [], standard_deadlines: [] });
  const [declarations, setDeclarations] = useState([]);
  const [registers, setRegisters] = useState([]);
  const [realtime, setRealtime] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    loadData();
  }, [tab, year, month, currentCompany?.id]);

  async function loadData() {
    setLoading(true);
    const cid = currentCompany?.id;
    const cidParam = cid ? `&company_id=${cid}` : '';
    try {
      if (tab === 'deadlines') {
        const { data } = await api.get(`/accounting/admin/deadlines?${cid ? `company_id=${cid}` : ''}`);
        setDeadlines(data);
      } else if (tab === 'declarations') {
        const { data } = await api.get(`/accounting/admin/declarations?year=${year}${cidParam}`);
        setDeclarations(data);
      } else if (tab === 'registers') {
        const { data } = await api.get(`/accounting/admin/registers?year=${year}&month=${month}${cidParam}`);
        setRegisters(data);
      } else if (tab === 'realtime') {
        const { data } = await api.get(`/accounting/admin/realtime?year=${year}&month=${month}${cidParam}`);
        setRealtime(data);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function approveDeclaration(id) {
    try {
      await api.post(`/accounting/admin/approve-declaration/${id}`);
      loadData();
    } catch (e) {
      alert(e.userMessage || 'Ошибка');
    }
  }

  async function approveRegister(id) {
    try {
      await api.post(`/accounting/admin/approve-register/${id}`);
      loadData();
    } catch (e) {
      alert(e.userMessage || 'Ошибка');
    }
  }

  const statusColors = {
    pending: '#f59e0b',
    approved: '#10b981',
    submitted: '#3b82f6',
    paid: '#6b7280',
  };

  const statusLabels = {
    pending: '⏳ Ожидает',
    approved: '✅ Одобрено',
    submitted: '📤 Отправлено',
    paid: '💰 Оплачено',
  };

  const months = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];

  return (
    <div className="page">
      <BackButton />
      <h1>📊 Бухгалтерия</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { id: 'deadlines', label: '⏰ Сроки' },
          { id: 'declarations', label: '📄 Декларации' },
          { id: 'registers', label: '📋 Реестры' },
          { id: 'realtime', label: '⚡ Налоги (реал‑тайм)' },
        ].map(opt => (
          <button
            key={opt.id}
            className={`btn ${tab === opt.id ? 'btn-primary' : ''}`}
            style={{ background: tab === opt.id ? undefined : 'rgba(255,255,255,0.1)' }}
            onClick={() => setTab(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Period Selector */}
      {tab !== 'deadlines' && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <select
            value={month}
            onChange={(e) => setMonth(parseInt(e.target.value))}
            style={{
              padding: 8, borderRadius: 8,
              background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none',
            }}
          >
            {months.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            style={{
              padding: 8, borderRadius: 8,
              background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none',
            }}
          >
            {[2024, 2025, 2026, 2027].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <div className="loader">{t('common.loading')}</div>
      ) : (
        <>
          {/* Deadlines Tab */}
          {tab === 'deadlines' && (
            <div>
              <h3>⏰ Ближайшие сроки</h3>
              
              {deadlines.upcoming_declarations.length > 0 ? (
                <div style={{ marginBottom: 24 }}>
                  {deadlines.upcoming_declarations.map(d => (
                    <div key={d.id} style={{
                      background: d.days_left <= 5 ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.05)',
                      borderRadius: 12,
                      padding: 16,
                      marginBottom: 8,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{d.type}</div>
                          <div style={{ fontSize: 13, opacity: 0.7 }}>Компания #{d.company_id}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 'bold', color: d.days_left <= 5 ? '#ef4444' : '#fff' }}>
                            {d.days_left} дн.
                          </div>
                          <div style={{ fontSize: 12 }}>{d.due_date}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                        <span style={{ fontSize: 18, fontWeight: 'bold' }}>{d.amount.toFixed(2)} PLN</span>
                        {d.status === 'pending' && (
                          <button className="btn btn-small btn-primary" onClick={() => approveDeclaration(d.id)}>
                            ✅ Одобрить
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 24, opacity: 0.5 }}>
                  Нет ближайших сроков
                </div>
              )}

              <h3>📅 Стандартные сроки</h3>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 12, padding: 16 }}>
                {deadlines.standard_deadlines.map((d, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: i < deadlines.standard_deadlines.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                  }}>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>{d.name}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>{d.description}</div>
                    </div>
                    <div style={{ fontWeight: 'bold' }}>{d.day}-го</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Declarations Tab */}
          {tab === 'declarations' && (
            <div>
              {declarations.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, opacity: 0.5 }}>
                  Нет деклараций за {year} год
                </div>
              ) : (
                declarations.map(d => (
                  <div key={d.id} style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>{d.declaration_type}</div>
                        <div style={{ fontSize: 13, opacity: 0.7 }}>
                          {months[d.month - 1]} {d.year} • Компания #{d.company_id}
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        background: statusColors[d.status],
                      }}>
                        {statusLabels[d.status]}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, opacity: 0.7 }}>Срок: {d.due_date}</div>
                        <div style={{ fontSize: 18, fontWeight: 'bold' }}>{d.tax_amount.toFixed(2)} PLN</div>
                      </div>
                      {d.status === 'pending' && (
                        <button className="btn btn-small btn-primary" onClick={() => approveDeclaration(d.id)}>
                          ✅ Одобрить
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Registers Tab */}
          {tab === 'registers' && (
            <div>
              {registers.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 32, opacity: 0.5 }}>
                  Нет реестров за {months[month - 1]} {year}
                </div>
              ) : (
                registers.map(r => (
                  <div key={r.id} style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 8,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontWeight: 'bold' }}>Реестр {r.register_type}</div>
                        <div style={{ fontSize: 13, opacity: 0.7 }}>Компания #{r.company_id}</div>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: 4,
                        fontSize: 11,
                        background: statusColors[r.status],
                      }}>
                        {statusLabels[r.status] || r.status}
                      </span>
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
                      <div style={{ background: 'rgba(255,255,255,0.05)', padding: 8, borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>VAT należny</div>
                        <div style={{ fontWeight: 'bold' }}>{r.vat_sales.toFixed(2)}</div>
                      </div>
                      <div style={{ background: 'rgba(255,255,255,0.05)', padding: 8, borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>VAT naliczony</div>
                        <div style={{ fontWeight: 'bold' }}>{r.vat_purchases.toFixed(2)}</div>
                      </div>
                      <div style={{ background: 'rgba(99,102,241,0.2)', padding: 8, borderRadius: 8, textAlign: 'center' }}>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>VAT do zapłaty</div>
                        <div style={{ fontWeight: 'bold', color: '#6366f1' }}>{r.vat_due.toFixed(2)}</div>
                      </div>
                    </div>

                    {r.status === 'pending' && (
                      <button className="btn btn-small btn-primary" onClick={() => approveRegister(r.id)}>
                        ✅ Одобрить реестр
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Realtime Tab */}
          {tab === 'realtime' && (
            <div>
              {!realtime ? (
                <div className="loader">{t('common.loading')}</div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>VAT к уплате</div>
                      <div style={{ fontWeight: 'bold' }}>{(realtime.vat?.due || 0).toFixed(2)} PLN</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>CIT к уплате</div>
                      <div style={{ fontWeight: 'bold' }}>{(realtime.cit?.due || 0).toFixed(2)} PLN</div>
                    </div>
                    <div style={{ background: 'rgba(255,255,255,0.05)', padding: 12, borderRadius: 8, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, opacity: 0.7 }}>Zaliczki</div>
                      <div style={{ fontWeight: 'bold' }}>{(((realtime.zaliczki?.pit || 0) + (realtime.zaliczki?.cit || 0))).toFixed(2)} PLN</div>
                    </div>
                  </div>

                  <div style={{ background: 'rgba(255,255,255,0.05)', padding: 16, borderRadius: 12, marginBottom: 12 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>📈 Оборот / Расходы</div>
                    <div>Оборот: <b>{(realtime.turnover || 0).toFixed(2)} PLN</b></div>
                    <div>Расходы: <b>{(realtime.expenses || 0).toFixed(2)} PLN</b></div>
                  </div>

                  <div style={{ background: 'rgba(255,255,255,0.05)', padding: 16, borderRadius: 12, marginBottom: 12 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: 8 }}>🧠 Оптимизация</div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {(realtime.optimization || []).map((opt, i) => (
                        <li key={i} style={{ marginBottom: 6 }}>{t}</li>
                      ))}
                    </ul>
                  </div>

                  {realtime.notes?.length > 0 && (
                    <div style={{ fontSize: 12, opacity: 0.7 }}>
                      {realtime.notes.join(' ')}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
